import React, { useState, useEffect } from 'react';
import { ChevronRight, Home, Upload } from 'lucide-react';

const Breadcrumbs = ({ 
  folders, 
  currentFolderId, 
  onFolderSelect,
  onMoveDocument
}) => {
  const [dragOverBreadcrumb, setDragOverBreadcrumb] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Listen for global drag events to show drop zones
  useEffect(() => {
    const handleDragStart = () => setIsDragging(true);
    const handleDragEnd = () => setIsDragging(false);
    
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);
    
    return () => {
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, []);
  // Build the breadcrumb path
  const buildBreadcrumbPath = () => {
    if (!currentFolderId) return [];
    
    const path = [];
    let currentFolder = folders.find(f => f.id === currentFolderId);
    
    // Build path from current folder back to root
    while (currentFolder) {
      path.unshift(currentFolder);
      currentFolder = folders.find(f => f.id === currentFolder.parentId);
    }
    
    return path;
  };

  const breadcrumbPath = buildBreadcrumbPath();
  
  // Drag and drop handlers
  const handleDragOver = (e, targetFolderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBreadcrumb(targetFolderId);
  };

  const handleDragLeave = () => {
    setDragOverBreadcrumb(null);
  };

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();
    setDragOverBreadcrumb(null);
    
    // Get the dragged document ID from the drag event
    const draggedDocumentId = e.dataTransfer.getData('text/plain');
    
    if (draggedDocumentId && onMoveDocument) {
      try {
        await onMoveDocument(draggedDocumentId, targetFolderId);
      } catch (error) {
        console.error('Error moving document via breadcrumb:', error);
      }
    }
  };
  
  if (!currentFolderId) return null;

  return (
    <div className="flex items-center space-x-1 text-sm text-gray-600 mb-4 px-1">
      {/* Home/Root */}
      <button
        onClick={() => onFolderSelect(null)}
        onDragOver={(e) => handleDragOver(e, null)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, null)}
        className={`flex items-center transition-all duration-200 px-3 py-2 rounded-lg relative ${
          dragOverBreadcrumb === null
            ? 'bg-blue-100 text-blue-700 border-2 border-blue-300 shadow-lg scale-105'
            : isDragging
            ? 'bg-blue-50 text-blue-600 border-2 border-dashed border-blue-300 hover:bg-blue-100'
            : 'hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <Home size={14} className="mr-2" />
        <span>Documents</span>
        {isDragging && dragOverBreadcrumb !== null && (
          <Upload size={12} className="ml-1 opacity-60" />
        )}
      </button>
      
      {/* Breadcrumb path */}
      {breadcrumbPath.map((folder, index) => (
        <React.Fragment key={folder.id}>
          <ChevronRight size={14} className="text-gray-400" />
          <button
            onClick={() => onFolderSelect(folder.id)}
            onDragOver={(e) => handleDragOver(e, folder.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, folder.id)}
            className={`px-3 py-2 rounded-lg transition-all duration-200 relative ${
              dragOverBreadcrumb === folder.id
                ? 'bg-blue-100 text-blue-700 border-2 border-blue-300 shadow-lg scale-105'
                : isDragging
                ? 'bg-blue-50 text-blue-600 border-2 border-dashed border-blue-300 hover:bg-blue-100'
                : index === breadcrumbPath.length - 1
                ? 'text-gray-900 font-medium'
                : 'hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {folder.name}
            {isDragging && dragOverBreadcrumb !== folder.id && (
              <Upload size={12} className="ml-1 opacity-60" />
            )}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default Breadcrumbs; 